import { forwardRef, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, Flex, Skeleton } from '@chakra-ui/react'
import rootStore from '../../../store/RootStore'
import ChartAnnotationLayer from './ChartAnnotationLayer'
import { observer } from 'mobx-react'
import { Highcharts } from '../../../setupHighcharts'
import HighchartsReact from 'highcharts-react-official'
import ChartHeader from './ChartHeader'
import ChartFilters from './ChartFilters'
import { enhanceChartOptions } from '../../../utils/ChartHelper'
import Table from './Table/Table'

export const Chart = observer(({ chart, onClick }) => {
  const {
    reportStore: { viewType },
    chartStore: { charts, registerChartInstance, unregisterChartInstance },
  } = rootStore
  const chartRef = useRef(null)
  const highchartComponent = useRef(null)

  // `chart` is a dependency because the table branch below renders no HighchartsReact at all, so the
  // ref legitimately changes when the chart object does. Re-registering is idempotent.
  useEffect(() => {
    if (!highchartComponent.current || !chart?.id) return
    registerChartInstance(chart.id, highchartComponent.current)
    return () => unregisterChartInstance(chart.id)
  }, [registerChartInstance, unregisterChartInstance, chart, chart?.id])

  useEffect(() => {
    const component = chartRef.current
    window.addEventListener('resize', () => {
      component?.chart?.reflow()
    })
    component?.chart?.reflow()
    return () => {
      window.removeEventListener('resize', () => {
        component?.chart?.reflow()
      })
    }
  }, [onClick, viewType.value])

  if (!chart || chart?.error)
    return (
      <ChartContainer
        viewType={viewType}
        id={chart?.id}
        ref={chartRef}>
        <ChartLoadingSkeleton />
      </ChartContainer>
    )

  if (charts[chart?.id]?.chartOptions.chart.type === 'table')
    return (
      <ChartContainer
        viewType={viewType}
        id={chart?.id}
        ref={chartRef}>
        <Box>
          {chart.filters?.some(filter => filter.options) && (
            <Flex
              mt="-30px"
              pb="6px"
              mb="12px"
              borderBottom="1px solid #E2E8F0"
              w="100%"
              justifyContent="flex-end">
              <ChartFilters filters={chart.filters} />
            </Flex>
          )}
          <ChartHeader
            title={chart.header}
            subHeader={chart.subHeader} // Pass the subheader here
            id={chart.id}
            filterIds={chart.filters}
            numberOfComments={chart.numberOfComments}
            chartComponentRef={highchartComponent}
          />
        </Box>
        {charts[chart.id]?.drillDownInfo?.linkages?.values && (
          <DrillDownMarker />
        )}
        <ChartAnnotationLayer
          id={chart.id}
          bounds={getOffset(chartRef.current)}
        />
        <Table options={charts[chart.id]?.chartOptions} />
      </ChartContainer>
    )

  // Apply both Y-axis formatting and plot padding
  const formattedChartOptions = enhanceChartOptions(
    charts[chart.id]?.chartOptions
  )

  return (
    <ChartContainer
      viewType={viewType}
      id={chart?.id}
      ref={chartRef}>
      <Box>
        {chart.filters?.some(filter => filter.options) && (
          <Flex
            mt="-30px"
            pb="6px"
            mb="12px"
            borderBottom="1px solid #E2E8F0"
            w="100%"
            justifyContent="flex-end">
            <ChartFilters filters={chart.filters} />
          </Flex>
        )}
        <ChartHeader
          title={chart.header}
          subHeader={chart.subHeader} // Pass the subheader here
          id={chart.id}
          filterIds={chart.filters}
          numberOfComments={chart.numberOfComments}
          chartComponentRef={highchartComponent}
        />
      </Box>
      {charts[chart.id]?.drillDownInfo?.linkages?.values && <DrillDownMarker />}
      <ChartAnnotationLayer
        id={chart.id}
        bounds={getOffset(chartRef.current)}
      />
      <HighchartsReact
        highcharts={Highcharts}
        options={formattedChartOptions}
        ref={highchartComponent}
      />
    </ChartContainer>
  )
})

Chart.propTypes = {
  chart: PropTypes.any,
}

Chart.displayName = 'Chart'

const ChartContainer = observer(
  forwardRef(({ id, viewType, ...props }, ref) => {
    const {
      commentStore: { chartId },
    } = rootStore
    return (
      <Box
        w={{
          base: '100%',
          lg: viewType.value === '2x2' ? '100%' : '660px',
        }}
        // maxW="700px"
        p={{ base: '24px', md: '32px' }}
        borderRadius="8px"
        backgroundColor="white"
        position="relative"
        border="2px solid"
        bg="white"
        borderColor={chartId && chartId === id ? 'orange' : 'transparent'}
        boxShadow={{
          base: 'none',
          md:
            chartId && chartId === id
              ? '0 8px 14px 0 rgba(0,0,0,0.1)'
              : '0 8px 14px 0 rgba(0,0,0,0.1)',
        }}
        ref={ref}
        {...props}
      />
    )
  })
)

ChartContainer.displayName = 'ChartContainer'

const DrillDownMarker = () => {
  return (
    <Box
      bottom={0}
      right={0}
      _after={{
        position: 'absolute',
        content: '""',
        zIndex: 0,
        bottom: '-2px',
        right: '-2px',
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '0 0 16px 16px',
        borderRightColor: 'brand.orange',
        borderBottomColor: 'brand.orange',
        borderTopColor: 'transparent',
        borderLeftColor: 'transparent',
      }}
    />
  )
}

const ChartLoadingSkeleton = () => (
  <Skeleton
    height="488px"
    width="100%"
    sx={{ animation: 'none !important', background: '#e2e8f0 !important' }}
  />
)

function getOffset(el) {
  if (!el) return
  const rect = el.getBoundingClientRect()
  const posX = rect.left + window.scrollX
  const posY = rect.top + window.scrollY
  return { left: posX, top: posY, width: rect.width, height: rect.height }
}
